import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ShieldCheck, ShieldOff, Copy, Check } from "lucide-react";

export default function MfaSetup() {
  const { user, refresh } = useAuth();
  const [step, setStep] = useState("idle"); // idle | provisioned | enabled
  const [secret, setSecret] = useState("");
  const [uri, setUri] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user?.mfa_enabled) setStep("enabled");
  }, [user]);

  const provision = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSecret(data.secret);
      setUri(data.otpauth_uri);
      setStep("provisioned");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const enable = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/mfa/enable", { code });
      await refresh();
      setStep("enabled");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    setError("");
    const c = prompt("Enter current 6-digit code to disable MFA:");
    if (!c) return;
    setLoading(true);
    try {
      await api.post("/auth/mfa/disable", { code: c });
      await refresh();
      setStep("idle");
      setSecret(""); setUri(""); setCode("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const copySecret = async () => {
    await navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <AppShell>
      <div className="p-8 max-w-3xl" data-testid="mfa-page">
        <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-[0.25em] mb-3">
          Security · /auth/mfa
        </div>
        <h1 className="font-display font-extrabold tracking-tight text-3xl mb-2">
          Two-factor authentication
        </h1>
        <p className="text-sm text-muted-foreground mb-8 max-w-xl">
          Add an extra layer to your account with a time-based one-time
          password (TOTP) compatible with Google Authenticator, 1Password,
          Authy, etc.
        </p>

        <div className="border border-border rounded-md">
          <div className="flex items-center justify-between p-5 border-b border-border">
            <div className="flex items-center gap-3">
              {user?.mfa_enabled ? (
                <ShieldCheck className="h-5 w-5 text-[hsl(var(--success))]" />
              ) : (
                <ShieldOff className="h-5 w-5 text-muted-foreground" />
              )}
              <div>
                <div className="text-sm font-semibold">TOTP Authenticator</div>
                <div className="text-xs text-muted-foreground">
                  Status: {user?.mfa_enabled ? "Enabled" : "Not configured"}
                </div>
              </div>
            </div>
            <Badge variant={user?.mfa_enabled ? "default" : "outline"} data-testid="mfa-status-badge">
              {user?.mfa_enabled ? "ACTIVE" : "INACTIVE"}
            </Badge>
          </div>

          <div className="p-5 space-y-4">
            {step === "idle" && !user?.mfa_enabled && (
              <>
                <p className="text-sm text-muted-foreground">
                  When enabled, you&apos;ll be asked for a 6-digit code from
                  your authenticator app after entering your password.
                </p>
                <Button onClick={provision} disabled={loading} data-testid="mfa-setup-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Set up authenticator
                </Button>
              </>
            )}

            {step === "provisioned" && (
              <form onSubmit={enable} className="space-y-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    Step 1 — Add to your app
                  </div>
                  <p className="text-sm">Scan the QR or paste this secret manually:</p>
                  <div className="flex items-center gap-2 mt-2">
                    <code className="flex-1 font-mono text-xs bg-secondary border border-border rounded px-3 py-2" data-testid="mfa-secret">
                      {secret}
                    </code>
                    <Button size="sm" variant="outline" type="button" onClick={copySecret} data-testid="copy-mfa-secret">
                      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                  <a
                    href={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(uri)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-3 text-xs text-primary hover:underline"
                  >
                    Open QR code in new tab →
                  </a>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="code" className="text-xs font-semibold uppercase tracking-wider">
                    Step 2 — Enter 6-digit code
                  </Label>
                  <Input
                    id="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    maxLength={6}
                    className="h-11 font-mono tracking-widest text-center text-lg max-w-[180px]"
                    data-testid="mfa-enable-code"
                  />
                </div>
                {error && (
                  <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">
                    {error}
                  </div>
                )}
                <Button type="submit" disabled={loading || code.length < 6} data-testid="mfa-enable-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Activate two-factor
                </Button>
              </form>
            )}

            {step === "enabled" && (
              <>
                <p className="text-sm">
                  Two-factor authentication is active on your account. You
                  will be prompted for a code on every sign in.
                </p>
                <Button variant="destructive" onClick={disable} disabled={loading} data-testid="mfa-disable-button">
                  Disable two-factor
                </Button>
                {error && (
                  <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2 mt-2">
                    {error}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
